import type { Key } from "react-aria-components";

import { zodResolver } from "@hookform/resolvers/zod";
import { UserIcon } from "lucide-react";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";

import { FieldError, Input, Label, TextField } from "@/components/field";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";

import type {
  AchPaymentMethod,
  PaymentMethod,
  PaymentMethodFormProps,
} from "../types";

import { ACCOUNT_TYPE_OPTIONS } from "../utils/payment-method-format";
import {
  type AchFormValues,
  createAchFormSchema,
  digitsOnly,
} from "../utils/payment-method-schema";

const MOCK_BANK_NAME = "Stripe Test Bank";

type NumericFieldName =
  | "accountNumber"
  | "confirmAccountNumber"
  | "routingNumber";

export function AchPaymentForm({
  currentMethod,
  formId,
  onSubmit,
  onValidityChange,
}: PaymentMethodFormProps<AchPaymentMethod>) {
  const schema = useMemo(
    () =>
      createAchFormSchema({
        currentRoutingNumber: currentMethod?.routingNumber,
      }),
    [currentMethod],
  );
  const { control, formState, handleSubmit, trigger } = useForm<AchFormValues>({
    defaultValues: {
      accountHolderName: currentMethod?.accountHolderName ?? "",
      accountNumber: "",
      accountType: currentMethod?.accountType,
      confirmAccountNumber: "",
      routingNumber: currentMethod?.routingNumber ?? "",
    },
    mode: "onChange",
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    onValidityChange(formState.isValid);
  }, [formState.isValid, onValidityChange]);

  const submitValues = (values: AchFormValues) =>
    onSubmit(
      async (): Promise<PaymentMethod> => ({
        accountHolderName: values.accountHolderName.trim(),
        accountType: values.accountType,
        bankName:
          currentMethod?.routingNumber === values.routingNumber
            ? currentMethod.bankName
            : MOCK_BANK_NAME,
        id: crypto.randomUUID(),
        last4: values.accountNumber
          ? values.accountNumber.slice(-4)
          : (currentMethod?.last4 ?? ""),
        routingNumber: values.routingNumber,
        type: "ach",
      }),
    );

  const renderNumericField = (
    name: NumericFieldName,
    label: string,
    placeholder: string,
    maxLength: number,
  ) => (
    <Controller
      control={control}
      name={name}
      render={({
        field: { onBlur, onChange, ref, value },
        fieldState: { error, invalid },
      }) => (
        <TextField
          isInvalid={invalid}
          onBlur={onBlur}
          onChange={(nextValue) => {
            onChange(digitsOnly(nextValue, maxLength));
            if (name === "accountNumber") void trigger("confirmAccountNumber");
          }}
          validationBehavior="aria"
          value={value}
        >
          <Label className="text-text-primary">{label}</Label>
          <Input inputMode="numeric" placeholder={placeholder} ref={ref} />
          <FieldError>{error?.message}</FieldError>
        </TextField>
      )}
    />
  );

  return (
    <form
      className="flex flex-col gap-xl"
      id={formId}
      onSubmit={handleSubmit(submitValues)}
    >
      <Controller
        control={control}
        name="accountHolderName"
        render={({
          field: { onBlur, onChange, ref, value },
          fieldState: { error, invalid },
        }) => (
          <TextField
            isInvalid={invalid}
            onBlur={onBlur}
            onChange={onChange}
            validationBehavior="aria"
            value={value}
          >
            <Label className="text-text-primary">Account holder name</Label>
            <div className="relative">
              <UserIcon
                aria-hidden="true"
                className="pointer-events-none absolute left-[14px] top-1/2 -translate-y-1/2 text-fg-quaternary"
                size={20}
              />
              <Input
                autoComplete="name"
                className="pl-[42px]"
                placeholder="John Doe"
                ref={ref}
              />
            </div>
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />

      <div className="grid grid-cols-1 gap-xl sm:grid-cols-2">
        <Controller
          control={control}
          name="accountType"
          render={({
            field: { onBlur, onChange, value },
            fieldState: { error, invalid },
          }) => (
            <Select
              isInvalid={invalid}
              onBlur={onBlur}
              onSelectionChange={(key: Key | null) => onChange(key)}
              placeholder="Select an account type"
              selectedKey={value ?? null}
              validationBehavior="aria"
            >
              <Label className="text-text-primary">Account type</Label>
              <SelectButton />
              <FieldError>{error?.message}</FieldError>
              <SelectPopover>
                <SelectListBox items={ACCOUNT_TYPE_OPTIONS}>
                  {(option) => (
                    <SelectListItem id={option.id}>
                      {option.label}
                    </SelectListItem>
                  )}
                </SelectListBox>
              </SelectPopover>
            </Select>
          )}
        />

        {renderNumericField("routingNumber", "Routing number", "9 digits", 9)}

        {renderNumericField(
          "accountNumber",
          "Account number",
          currentMethod
            ? `•••• ${currentMethod.last4}`
            : "Enter account number",
          17,
        )}

        {renderNumericField(
          "confirmAccountNumber",
          "Confirm account number",
          "Re-enter account number",
          17,
        )}
      </div>
    </form>
  );
}
